import { useState } from "react";
import type { Transaction, MainCategory } from "../types";
import { formatSEK, formatDate, transactionTypeLabels, bankLabels } from "../lib/format";
import { CategoryBadge } from "./CategoryBadge";
import { useCategories } from "../hooks/useCategories";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ArrowLeftRight, Calculator, Eye } from "lucide-react";

interface TransactionTableProps {
  transactions: Transaction[];
  onUpdateCategory?: (
    id: string,
    category: MainCategory,
    subcategory: string
  ) => Promise<void>;
}

export function TransactionTable({
  transactions,
  onUpdateCategory,
}: TransactionTableProps) {
  const { allCategories, getSubcategories, refresh } = useCategories();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editCategory, setEditCategory] = useState<string>("other");
  const [editSubcategory, setEditSubcategory] = useState<string>("");

  if (transactions.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        Inga transaktioner att visa
      </div>
    );
  }

  function startEditing(tx: Transaction) {
    setEditingId(tx.id);
    setEditCategory(tx.category);
    setEditSubcategory(tx.subcategory);
  }

  const currentSubcats = getSubcategories(editCategory);

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b text-left">
            <th className="pb-2 font-medium text-muted-foreground">Datum</th>
            <th className="pb-2 font-medium text-muted-foreground">
              Beskrivning
            </th>
            <th className="pb-2 font-medium text-muted-foreground text-right">
              Belopp
            </th>
            <th className="pb-2 font-medium text-muted-foreground">Typ</th>
            <th className="pb-2 font-medium text-muted-foreground">
              Kategori
            </th>
            <th className="pb-2 font-medium text-muted-foreground">Konto</th>
            <th className="pb-2 font-medium text-muted-foreground">Totalsumma</th>
          </tr>
        </thead>
        <tbody>
          {transactions.map((tx) => (
            <tr
              key={tx.id}
              className={`border-b border-border/50 hover:bg-muted/50 ${
                tx.isTransfer || (tx.amount < 0 && !tx.includedInTotals) ? "opacity-65" : ""
              }`}
            >
              <td className="py-2.5 pr-4 tabular-nums whitespace-nowrap">
                {formatDate(tx.date)}
              </td>
              <td className="py-2.5 pr-4 max-w-[300px] truncate">
                <div className="flex items-center gap-2">
                  {tx.isTransfer && (
                    <ArrowLeftRight className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                  )}
                  <span title={tx.description}>{tx.description}</span>
                </div>
              </td>
              <td
                className={`py-2.5 pr-4 text-right tabular-nums whitespace-nowrap font-medium ${
                  tx.amount < 0 ? "text-destructive" : "text-success"
                }`}
              >
                {tx.amount > 0 ? "+" : ""}
                {formatSEK(tx.amount)}
              </td>
              <td className="py-2.5 pr-4 text-xs whitespace-nowrap">
                <span className="rounded-full bg-muted px-2 py-0.5 text-muted-foreground">
                  {transactionTypeLabels[tx.transactionType ?? (tx.amount >= 0 ? "income" : "expense")] ?? "Annat"}
                </span>
              </td>
              <td className="py-2.5 pr-4">
                {editingId === tx.id && onUpdateCategory ? (
                  <div className="flex items-center gap-1">
                    <Select
                      value={editCategory}
                      onValueChange={(val) => {
                        setEditCategory(val);
                        setEditSubcategory("");
                      }}
                    >
                      <SelectTrigger className="h-7 w-[130px] text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {allCategories.map((cat) => (
                          <SelectItem key={cat.value} value={cat.value}>
                            {cat.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select
                      value={editSubcategory}
                      onValueChange={async (val) => {
                        setEditSubcategory(val);
                        await onUpdateCategory!(
                          tx.id,
                          editCategory as MainCategory,
                          val
                        );
                        await refresh();
                        setEditingId(null);
                      }}
                    >
                      <SelectTrigger className="h-7 w-[130px] text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {currentSubcats.map((sub) => (
                          <SelectItem key={sub.value} value={sub.value}>
                            {sub.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ) : (
                  <CategoryBadge
                    category={tx.category}
                    subcategory={tx.subcategory}
                    showSubcategory
                    onClick={
                      onUpdateCategory
                        ? () => startEditing(tx)
                        : undefined
                    }
                  />
                )}
              </td>
              <td className="py-2.5 text-muted-foreground text-xs">
                <div className="font-medium uppercase">
                  {bankLabels[tx.bank] ?? tx.bank}
                </div>
                {(tx.accountName || tx.accountNumber) && (
                  <div className="normal-case truncate max-w-[180px]" title={[tx.accountName, tx.accountNumber].filter(Boolean).join(" · ")}>
                    {[tx.accountName, tx.accountNumber].filter(Boolean).join(" · ")}
                  </div>
                )}
              </td>
              <td className="py-2.5 text-xs whitespace-nowrap">
                {tx.amount >= 0 ? (
                  <span className="rounded-full bg-muted px-2 py-0.5 text-muted-foreground">
                    Ej utgift
                  </span>
                ) : tx.includedInTotals ? (
                  <span className="inline-flex items-center gap-1 rounded-full bg-success/10 px-2 py-0.5 text-success">
                    <Calculator className="h-3 w-3" />
                    Räknas
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-muted-foreground">
                    <Eye className="h-3 w-3" />
                    Visas bara
                  </span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
